import { Box, Flex, useTheme } from '@chakra-ui/react'
import { LangCode } from 'api-client'
import React from 'react'
import { useSelector } from 'react-redux'

import { LanguageSelection } from '~shared-components/components/LanguageSelection/LanguageSelection'
import { RouterLink } from '~shared-components/components/RouterLink/RouterLink'
import { useSelectUserLanguage } from '~shared-components/hooks/useSelectUserLanguage'
import { useTranslations } from '~shared-components/hooks/useTranslations'
import { LocationSelector } from '~shared-components/redux/selectors/location.selector'
import { LoginSelector } from '~shared-components/redux/selectors/login.selector'
import BrenntagLogo from '~shared-components/theme/svgs/brenntag_logo.svg'
import { TypeOfTerms } from '~shared-components/translations/terms/terms'
import { getContactUsMailtoLink } from '~shared-components/utils/contactEmailMapping'
import { Page, SupportedLanguage } from '~shared-components/utils/enums'

export enum FooterLinkType {
  External,
  Internal,
}

export type FooterLink = {
  label: string
  payload?: object
  target: object | string
  type: FooterLinkType
}

const renderLink = (item: FooterLink, index: number) => {
  switch (item.type) {
    case FooterLinkType.Internal:
      return (
        <RouterLink
          key={index}
          target="_blank"
          to={{
            payload: item.payload ? item.payload : {},
            type: item.target as Page,
          }}
        >
          {item.label}
        </RouterLink>
      )
    case FooterLinkType.External:
      return (
        <a
          key={index}
          href={item.target as string}
          rel="noopener noreferrer"
          target="_blank"
        >
          {item.label}
        </a>
      )
    default:
      break
  }
}

export const Footer = () => {
  const t = useTranslations()
  const locale = useSelector(LocationSelector.locale)
  const salesOrg = useSelector(LoginSelector.selectedSalesOrganization)
  const { isLoading, setUserLanguage } = useSelectUserLanguage()
  const { __cssMap } = useTheme()

  const localeCode = locale?.code.toLowerCase()
  const contactUsMailto = getContactUsMailtoLink(salesOrg)

  const onLanguageChange = async (selectedLanguage: SupportedLanguage) => {
    await setUserLanguage({
      language: (selectedLanguage as unknown) as LangCode,
    })
    return
  }

  const links = [
    {
      label: '© Brenntag SE',
      target: `https://www.brenntag.com/${localeCode}/`,
      type: FooterLinkType.External,
    },
    {
      label: t('footer.contactus'),
      target: contactUsMailto,
      type: FooterLinkType.External,
    },
    {
      label: t('footer.termsofuse'),
      payload: { typeOfTerms: TypeOfTerms.TermsOfUse },
      target: Page.TermsAndConditions,
      type: FooterLinkType.Internal,
    },
    {
      label: t('footer.faqs'),
      target: `https://www.brenntag.com/${localeCode}/services/brenntag-connect-online-services/faq/`,
      type: FooterLinkType.External,
    },
    {
      label: t('footer.privacypolicy'),
      target: `https://www.brenntag.com/${localeCode}/privacy-policy/`,
      type: FooterLinkType.External,
    },
  ]

  const childrenColor = __cssMap['colors.neutral.70']?.value
  const sxFooter = {
    '> *': { fontSize: 'md', my: 3 },
    '> a, > span a': {
      color: `${childrenColor} !important`,
      marginRight: [0, 10],
    },
  }

  return (
    <Box
      as="footer"
      backgroundColor="white"
      borderTop="1px"
      borderTopColor="neutral.10"
      paddingBottom={0}
      pt={[2, 2, 'unset']}
      px={[6, 6, 20]}
      w="100%"
    >
      <Flex
        alignItems={{ base: 'flex-start', sm: 'center' }}
        flexDirection={{ base: 'column', sm: 'row' }}
        justifyContent="flex-start"
        justifyItems={{ base: 'flex-start', sm: 'center' }}
        padding={3}
        sx={sxFooter}
        wrap="wrap"
      >
        {links.map((item, index) => renderLink(item, index))}

        <Box mr={{ base: 0, sm: 4 }} my={4}>
          <LanguageSelection
            showWhenOnlyOneLanguage
            isLoading={isLoading}
            onChange={onLanguageChange}
            placement="top"
          />
        </Box>

        <Box ml={[0, 0, 'auto']}>
          <BrenntagLogo h="48px" w="262px" />
        </Box>
      </Flex>
    </Box>
  )
}
